<template>
    <div>
        <header class="modal-card-head">
            <p class="modal-card-title">Preview Activity</p>
            <button type="button" class="delete" aria-label="close" @click="onClose" />
        </header>

        <div class="modal-card-body">
            <div class="content">
                <div class="breadcrumb-container">
                    <div class="breadcrumb-items">
                        <div class="breadcrumb-item">Courses</div>
                        <div v-if="courseTemplateTitle" class="breadcrumb-item">
                            {{ courseTemplateTitle }}
                        </div>

                        <div v-if="parentTitle" class="breadcrumb-item">
                            {{ parentTitle }}
                        </div>
                    </div>
                </div>

                <div class="my-4 tw:flex tw:flex-col tw:justify-center">
                    <div class="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
                        <component
                            :is="activityTitleIcon"
                            v-if="activityTitleIcon"
                            :size="24"
                            class="tw:flex-shrink-0"
                        />
                        <h1 v-if="activityTitlePrefix" class="title">{{ activityTitlePrefix }}: {{ ActivityTitle }}</h1>
                        <h1 v-else>{{ ActivityTitle }}</h1>
                    </div>

                    <div class="tw:flex tw:gap-2">
                        <e-button
                            :class="{ 'is-active': isInstructorView }"
                            type="secondary"
                            icon-left="pi pi-eye"
                            size="small"
                            @click="isInstructorView = true"
                        >
                            Instructor View
                        </e-button>

                        <e-button
                            :class="{ 'is-active': !isInstructorView }"
                            type="secondary"
                            icon-left="pi pi-eye"
                            size="small"
                            @click="isInstructorView = false"
                        >
                            Student View
                        </e-button>
                    </div>
                </div>

                <div class="columns is-desktop">
                    <div
                        class="column is-full-touch"
                        :class="isInstructorView || hasMedia ? 'is-8' : 'is-10'"
                    >
                        <div class="columns">
                            <div class="column">
                                <div v-if="isInstructorView">
                                    <div class="columns">
                                        <div class="column mb-3">
                                            <announcements
                                                type="Activity"
                                                announcement-key="testUpdates"
                                                :course-id="0"
                                                preview-mode
                                                @fulfilled="() => false"
                                            >
                                                <template #header>Updates</template>

                                                <template #action>Add Update</template>

                                                <template #empty>No updates for this activity.</template>
                                            </announcements>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="component === 'quiz'">
                                    <Quiz
                                        :uuid="uuid"
                                        :activity-prop="activity"
                                        :namespace="namespace"
                                        :route-props="quizRouteProps"
                                        read-only
                                        is-template-preview
                                        :is-instructor-view="isInstructorView"
                                    />
                                </div>

                                <div v-if="component !== 'quiz'">
                                    <component
                                        :is="component"
                                        :uuid="uuid"
                                        :activity="activity"
                                        :namespace="namespace"
                                        read-only
                                        class="preview-component"
                                    />
                                </div>
                                <div class="columns mb-4 mt-4">
                                    <div class="column">
                                        <e-button type="is-primary" @click.prevent="() => false">
                                            <span>Mark Complete</span>
                                        </e-button>
                                    </div>
                                </div>

                                <div class="columns">
                                    <div class="column">
                                        <div>
                                            <div class="columns is-multiline">
                                                <div class="column is-12">
                                                    <div class="content">
                                                        <h2 class="m-b-none">My Submissions</h2>
                                                    </div>
                                                </div>
                                                <div class="column">
                                                    <p class="has-text-subtle">
                                                        No submissions to
                                                        display.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div class="columns">
                                        <div class="column">
                                            <announcements
                                                type="Activity"
                                                announcement-key="testNote"
                                                :course-id="0"
                                                preview-mode
                                                preview-action-outline
                                                @fulfilled="() => false"
                                            >
                                                <template #header>My Notes</template>

                                                <template #action>Add Note</template>

                                                <template #empty>No notes to display.</template>
                                            </announcements>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        class="column is-full-touch"
                        :class="isInstructorView || hasMedia ? 'is-4' : 'is-hidden'"
                    >
                        <meta-panel v-if="isInstructorView || hasMedia">
                            <meta-category
                                icon="paperclip"
                                title="Attachments"
                                body-fallback="No attachments for this activity."
                                :can-collapse="false"
                            >
                                <attachments
                                    v-if="hasMedia"
                                    class="is-activity-attachments"
                                    label=""
                                    :media="activity.media"
                                />
                            </meta-category>
                        </meta-panel>
                        <meta-panel v-if="isInstructorView">
                            <meta-category
                                icon="key-skeleton-alt"
                                body-fallback=""
                                title="Attempt Authorizations"
                                :can-collapse="false"
                                class="mt-5"
                            >
                                <template #actions>
                                    <i class="uil uil-plus-circle is-size-4" />
                                </template>
                            </meta-category>
                        </meta-panel>
                    </div>
                </div>
            </div>
        </div>

        <footer class="modal-card-foot">
            <e-button type="is-primary" @click="onClose"> Close Preview</e-button>
        </footer>
    </div>
</template>

<script>
import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import components from '@/components/activity-types/course-activity-types/activities/Index.js';
import Announcements from '@/components/announcements/Announcements.vue';
import MetaCategory from '@/components/meta-panel/MetaCategory.vue';
import MetaPanel from '@/components/meta-panel/MetaPanel.vue';
import { getActivityDisplayIcon, getActivityDisplayPrefix } from '@/constants/activityDisplayMeta.js';
import CourseTemplate from '@/models/CourseTemplate.js';

export default {
    name: 'TemplatePreview',

    components: {
        Attachments,
        Announcements,
        MetaCategory,
        MetaPanel,
        ...components,
    },

    emits: ['close'],

    props: {
        uuid: {
            type: String,
            required: true,
        },

        isTemplate: {
            type: Boolean,
            default: false,
        },

        courseTitle: {
            type: String,
            default: '',
            required: false,
        },

        parentTitle: {
            type: String,
            default: '',
            required: false,
        },
    },

    data() {
        return {
            isInstructorView: true,
            previewTitle: '',
        };
    },

    computed: {
        activity() {
            return this.isTemplate
                ? this.$store.getters['ActivityTemplates/get'](this.uuid)
                : this.$store.getters['Activities/get'](this.uuid);
        },

        hasMedia() {
            return !!this.activity?.media?.length;
        },

        courseTemplateTitle() {
            return this.courseTitle || this.previewTitle;
        },

        component() {
            const { activity_type: activityType } = this.activity;

            if (activityType === 'video') return 'video-template';

            return activityType;
        },

        ActivityTitle() {
            return this.activity?.title;
        },

        activityTypeKey() {
            if (this.activity?.activity_type_key) return this.activity.activity_type_key;

            const at = this.activity?.activity_type;
            if (typeof at === 'string' && at) return at;
            if (at && typeof at === 'object' && at.key) return at.key;

            return null;
        },

        activityTitleIcon() {
            return getActivityDisplayIcon(this.activityTypeKey);
        },

        activityTitlePrefix() {
            const prefix = getActivityDisplayPrefix(this.activityTypeKey);
            return prefix !== 'Activity' ? prefix : '';
        },

        namespace() {
            return this.isTemplate ? 'ActivityTemplates' : 'Activities';
        },

        quizRouteProps() {
            // Provide route props needed by Quiz component for template preview
            const activityId = this.activity?.id || 0;
            const courseId = this.activity?.course_id || 1; // Use 1 as fallback for template preview

            return {
                activityId,
                courseId,
            };
        },
    },

    async created() {
        if (!this.courseTitle) {
            const id = this.$route?.params?.id;

            if (id) {
                const courseTemplate = await CourseTemplate.lazyFind(id);
                this.previewTitle = courseTemplate?.title ?? '';
            } else {
                this.previewTitle = '';
            }
        }
    },

    methods: {
        onClose() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.template-preview {
    height: 100%;
    min-height: 90vh;
    max-height: 90vh;
    overflow: hidden;
}

/* Global override for the modal's animation-content - this needs to be outside scoped */
.modal .template-preview-modal {
    .animation-content {
        max-width: none !important;
        width: 90vw !important;
    }
}

/* Alternative: Target the modal wrapper directly */
.modal:has(.template-preview-modal) .animation-content {
    max-width: none !important;
    width: 90vw !important;
}

/* Fallback for browsers that don't support :has() */
.modal .animation-content {
    &.template-preview-modal,
    .template-preview-modal & {
        max-width: none !important;
        width: 90vw !important;
    }
}

.breadcrumb-items {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.breadcrumb-item {
    position: relative;
    display: flex;
    align-items: center;
    padding-right: 2rem; // Make room for the absolute positioned chevron

    &::after {
        content: '\eb6d';
        font-family: 'unicons';
        font-size: 1.75rem;
        color: $primary;
        position: absolute;
        right: 0;
        top: 60%;
        transform: translateY(-50%);
        line-height: 1;
    }

    &:last-child {
        &::after {
            display: none;
        }
    }
}
</style>
